import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadVerbsCsv } from './utils/loadVerbsCsv';
import { conjugatePastGeneric_Salim } from './utils/arPastGeneric_salim';
import { conjugatePresentFormI_Salim_fromRow } from './utils/arPresentFormI_salim';
import { buildImperativeFromRow } from './utils/imperativeFormI_salim';
import html2canvas from 'html2canvas';
import alarmClockIcon from './imgs/alarm-clock.svg';
import './VerbTenseGame1.css';

const FORMS = [
  { id: 'עבר', name: 'עבר' },
  { id: 'הווה-עתיד', name: 'הווה-עתיד' },
  { id: 'ציווי', name: 'ציווי' }
];

const PRONOUNS_PAST_FUTURE = [
  { arabic: 'أَنَا' },
  { arabic: 'أَنْتَ' },
  { arabic: 'أَنْتِ' },
  { arabic: 'هُوَ' },
  { arabic: 'هِيَ' },
  { arabic: 'نَحْنُ' },
  { arabic: 'أَنْتُمْ' },
  { arabic: 'أَنْتُنَّ' },
  { arabic: 'هُمْ' },
  { arabic: 'هُنَّ' }
];

const PRONOUNS_IMPERATIVE = [
  { id: 'you_ms', arabic: 'أَنْتَ' },
  { id: 'you_fs', arabic: 'أَنْتِ' },
  { id: 'you_mp', arabic: 'أَنْتُمْ' },
  { id: 'you_fp', arabic: 'أَنْتُنَّ' }
];

function VerbTenseGame1() {
  const navigate = useNavigate();
  const { grade } = useParams();
  const location = useLocation();
  const fromBinyan = location.state?.fromBinyan;
  
  const getBackUrl = () => {
    if (fromBinyan) {
      return `/grade/${grade}/verbs/binyan/${fromBinyan}`;
    }
    return `/grade/${grade}/verbs`;
  };
  
  const [gameVerbs, setGameVerbs] = useState([]);
  const [tenseOrder, setTenseOrder] = useState([]);
  const [currentVerbIndex, setCurrentVerbIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedLock, setSelectedLock] = useState(null);
  const [gameHistory, setGameHistory] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [draggedKey, setDraggedKey] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [draggedElement, setDraggedElement] = useState(null);
  const [hoveredLock, setHoveredLock] = useState(null);
  const [showResultsPopup, setShowResultsPopup] = useState(false);
  const [currentAttempts, setCurrentAttempts] = useState(0);
  const [hasError, setHasError] = useState(false);
  const resultsSummaryRef = useRef(null);
  
  const audioContextRef = useRef(null);
  
  const initAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
  };
  
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const [pronounAssignments, setPronounAssignments] = useState([]);

  const generateQuestion = useCallback((verbs, tenses, index, pronouns) => {
    if (index >= verbs.length) {
      setShowResults(true);
      return;
    }
    
    const verbData = verbs[index];
    const targetTense = tenses[index];
    const assignedPronoun = pronouns[index];
    
    let conjugatedVerb = null;
    let pronoun = null;
    
    if (targetTense === 'עבר') {
      const conjugations = conjugatePastGeneric_Salim(verbData['פועל']);
      if (!conjugations) return;
      
      pronoun = assignedPronoun;
      conjugatedVerb = conjugations[pronoun.arabic];
      
    } else if (targetTense === 'הווה-עתיד') {
      const conjugations = conjugatePresentFormI_Salim_fromRow(verbData);
      if (!conjugations) return;
      
      pronoun = assignedPronoun;
      conjugatedVerb = conjugations[pronoun.arabic];
      
    } else if (targetTense === 'ציווי') {
      const imperatives = buildImperativeFromRow(verbData);
      if (!imperatives) return;
      
      conjugatedVerb = imperatives[assignedPronoun.id];
      pronoun = { arabic: assignedPronoun.arabic };
    }
    
    if (!conjugatedVerb) return;
    
    setCurrentQuestion({
      verb: conjugatedVerb,
      verbData: verbData,
      correctForm: targetTense,
      pronoun: pronoun
    });
    
    setFeedback(null);
    setSelectedLock(null);
    setDraggedKey(false);
    setIsDragging(false);
    setDraggedElement(null);
    setHoveredLock(null);
    setCurrentAttempts(0);
    setHasError(false);
  }, []);

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const createBalancedTenseSequenceWithPronouns = () => {
    const tenses = ['עבר', 'הווה-עתיד', 'ציווי'];
    const tenseSequence = [];
    
    for (let i = 0; i < 3; i++) {
      tenseSequence.push('עבר');
    }
    for (let i = 0; i < 3; i++) {
      tenseSequence.push('הווה-עתיד');
    }
    for (let i = 0; i < 3; i++) {
      tenseSequence.push('ציווי');
    }
    
    const randomTense = tenses[Math.floor(Math.random() * tenses.length)];
    tenseSequence.push(randomTense);
    
    const shuffledPastPronouns = shuffleArray(PRONOUNS_PAST_FUTURE);
    const shuffledPresentPronouns = shuffleArray(PRONOUNS_PAST_FUTURE);
    const shuffledImperativePronouns = shuffleArray(PRONOUNS_IMPERATIVE);
    
    let pastIndex = 0;
    let presentIndex = 0;
    let imperativeIndex = 0;
    
    const pronounSequence = tenseSequence.map((tense) => {
      if (tense === 'עבר') {
        const pronoun = shuffledPastPronouns[pastIndex % shuffledPastPronouns.length];
        pastIndex++;
        return pronoun;
      } else if (tense === 'הווה-עתיד') {
        const pronoun = shuffledPresentPronouns[presentIndex % shuffledPresentPronouns.length];
        presentIndex++;
        return pronoun;
      } else {
        const pronoun = shuffledImperativePronouns[imperativeIndex % shuffledImperativePronouns.length];
        imperativeIndex++;
        return pronoun;
      }
    });
    
    const hasThreeConsecutiveForms = (sequence) =>
      sequence.some((tense, index) => index >= 2 && tense === sequence[index - 1] && tense === sequence[index - 2]);

    const sourceItems = tenseSequence.map((tense, index) => ({
      tense,
      pronoun: pronounSequence[index]
    }));

    let finalItems = null;
    for (let attempt = 0; attempt < 100; attempt++) {
      const candidate = shuffleArray(sourceItems);
      if (!hasThreeConsecutiveForms(candidate.map(item => item.tense))) {
        finalItems = candidate;
        break;
      }
    }

    if (!finalItems) {
      const fallbackTenses = ['עבר', 'הווה-עתיד', 'ציווי', 'עבר', 'הווה-עתיד', 'ציווי', 'עבר', 'הווה-עתיד', 'ציווי', randomTense];
      const remainingItems = [...sourceItems];
      finalItems = fallbackTenses.map((tense) => {
        const itemIndex = remainingItems.findIndex(item => item.tense === tense);
        const [item] = remainingItems.splice(itemIndex, 1);
        return item;
      });
    }
    
    const finalTenseSequence = finalItems.map(item => item.tense);
    const finalPronounSequence = finalItems.map(item => item.pronoun);
    
    return { tenseSequence: finalTenseSequence, pronounSequence: finalPronounSequence };
  };

  useEffect(() => {
    const loadVerbs = async () => {
      try {
        const rows = await loadVerbsCsv('/verbs-grade10.csv');
        
        const filteredVerbs = rows.filter(r => {
          const binyan = (r['בניין'] || '').trim();
          const gizra = (r['gizra'] || '').trim();
          const tense = r['זמן'];
          const faVowelFuture = r['R2_FUTURE'] || r['fa_vowel_future'];
          
          return (binyan === '1' || binyan === 'בניין 1') && 
                 gizra.includes('שלמים') && 
                 tense === 'עבר' &&
                 faVowelFuture && faVowelFuture.trim() !== '';
        });
        
        const shuffled = shuffleArray(filteredVerbs);
        const selected = shuffled.slice(0, 10);
        
        const { tenseSequence, pronounSequence } = createBalancedTenseSequenceWithPronouns();
        
        setGameVerbs(selected);
        setTenseOrder(tenseSequence);
        setPronounAssignments(pronounSequence);
        generateQuestion(selected, tenseSequence, 0, pronounSequence);
      } catch (error) {
        console.error('שגיאה בטעינת פעלים:', error);
      }
    };
    
    loadVerbs();
  }, [generateQuestion]);

  const handlePointerStart = (e) => {
    if (feedback === 'correct') return;
    
    e.preventDefault();
    setDraggedKey(true);
    setIsDragging(true);
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setDraggedElement(e.currentTarget);
    
    e.currentTarget.setPointerCapture(e.pointerId);
    e.currentTarget.style.zIndex = '1000';
  };

  const handlePointerMove = (e) => {
    if (!isDragging || !draggedElement) return;
    
    const dx = e.clientX - dragStartPos.x;
    const dy = e.clientY - dragStartPos.y;
    
    draggedElement.style.transform = `translate(${dx}px, ${dy}px)`;
    
    const locks = document.querySelectorAll('.lock');
    let foundHoveredLock = null;
    
    locks.forEach((lock) => {
      const rect = lock.getBoundingClientRect();
      const margin = 30;
      
      if (e.clientX >= rect.left - margin &&
          e.clientX <= rect.right + margin &&
          e.clientY >= rect.top - margin &&
          e.clientY <= rect.bottom + margin) {
        
        const formId = lock.getAttribute('data-form-id');
        if (formId) {
          foundHoveredLock = formId;
        }
      }
    });
    
    setHoveredLock(foundHoveredLock);
  };

  const handlePointerEnd = (e) => {
    if (!isDragging || !draggedElement) return;
    
    draggedElement.style.transform = '';
    draggedElement.style.zIndex = '';
    
    if (hoveredLock) {
      setSelectedLock(hoveredLock);
      checkAnswer(hoveredLock);
    }
    
    setIsDragging(false);
    setDraggedKey(false);
    setDraggedElement(null);
    setHoveredLock(null);
  };

  const checkAnswer = (formId) => {
    const isCorrect = formId === currentQuestion.correctForm;
    
    const updatedAttempts = currentAttempts + 1;
    setCurrentAttempts(updatedAttempts);
    
    setFeedback(isCorrect ? 'correct' : 'wrong');
    
    if (isCorrect) {
      playSuccessSound();
      setTimeout(() => {
        saveToHistory(updatedAttempts);
        nextQuestion();
      }, 600);
    } else {
      setHasError(true);
      playErrorSound();
      setTimeout(() => {
        setFeedback(null);
        setSelectedLock(null);
      }, 800);
    }
  };

  const saveToHistory = (finalAttempts) => {
    setGameHistory(prev => [...prev, {
      verb: currentQuestion.verb,
      verbData: currentQuestion.verbData,
      correctForm: currentQuestion.correctForm,
      userAnswer: selectedLock,
      attempts: finalAttempts,
      hadError: hasError
    }]);
  };

  const nextQuestion = () => {
    const nextIndex = currentVerbIndex + 1;
    setCurrentVerbIndex(nextIndex);
    generateQuestion(gameVerbs, tenseOrder, nextIndex, pronounAssignments);
  };

  const playSuccessSound = () => {
    try {
      initAudioContext();
      if (!audioContextRef.current) return;
      const audioContext = audioContextRef.current;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 523.25;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const playErrorSound = () => {
    try {
      initAudioContext();
      if (!audioContextRef.current) return;
      const audioContext = audioContextRef.current;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 200;
      oscillator.type = 'sawtooth';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const handleBackToGames = () => {
    navigate(getBackUrl());
  };

  const generateResultsImage = async () => {
    if (!resultsSummaryRef.current) return null;
    const canvas = await html2canvas(resultsSummaryRef.current, {
      backgroundColor: '#5eb9c5',
      scale: 2,
    });
    return canvas.toDataURL('image/png');
  };

  const dataURLtoBlob = (dataURL) => {
    const parts = dataURL.split(',');
    const mime = parts[0].match(/:(.*?);/)[1];
    const bstr = atob(parts[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new Blob([u8arr], { type: mime });
  };

  const shareViaWhatsApp = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;

    const blob = dataURLtoBlob(imageData);
    const fileName = 'תוצאות-מהו-הזמן.png';
    const file = new File([blob], fileName, { type: 'image/png' });

    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: 'תוצאות מהו הזמן?',
          text: `מהו הזמן?\nהשגתי ${gameHistory.filter(h => !h.hadError).length}/${gameHistory.length}!`,
          files: [file],
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('שגיאה בשיתוף:', err);
        }
      }
    } else {
      const link = document.createElement('a');
      link.download = fileName;
      link.href = URL.createObjectURL(blob);
      link.click();
    }
    setShowResultsPopup(false);
  };

  const shareViaEmail = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;
    const fileName = 'תוצאות-מהו-הזמן.png';
    const link = document.createElement('a');
    link.download = fileName;
    link.href = imageData;
    link.click();
    setShowResultsPopup(false);
  };

  if (showResults) {
    const correctCount = gameHistory.filter(h => !h.hadError).length;

    return (
      <div className="App">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={handleBackToGames} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>תוצאות המשחק</h1>
          </div>
        </header>
        <main className="game-container results-screen">
          <div className="words-summary" ref={resultsSummaryRef}>
            <h3>מהו הזמן?</h3>
            <div className="final-score-display">
              ניקוד: {correctCount}/{gameHistory.length}
            </div>
            <br/>
            <div className="words-list">
              {gameHistory.map((item, idx) => (
                <div key={idx} className="word-item">
                  <div className="word-pair">
                    <span className="arabic-word-summary">{item.verb}</span>
                    <span className="separator"> - </span>
                    <span className="hebrew-word">{item.correctForm}</span>
                  </div>
                  <div className={`attempts-info ${item.hadError ? 'multiple-attempts' : ''}`}>
                    {!item.hadError ? (
                      <span>✓ תשובה נכונה</span>
                    ) : (
                      <span>✗ נסיונות: {item.attempts}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="game-end-buttons">
              <button onClick={() => window.location.reload()} className="game-button restart-button">
                שחק מחדש
              </button>
              <button onClick={() => setShowResultsPopup(true)} className="game-button send-button">
                שתף תוצאות
              </button>
            </div>
          </div>

          {showResultsPopup && (
            <div className="popup-overlay" onClick={() => setShowResultsPopup(false)}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                <button className="popup-close-button" onClick={() => setShowResultsPopup(false)}>
                  ×
                </button>
                <h3>שתף את התוצאות</h3>
                <p>בחר איך תרצה לשתף את התוצאות שלך:</p>
                <div className="popup-buttons">
                  <button onClick={shareViaEmail} className="popup-button email-button">
                    💾 הורד תמונה
                  </button>
                  <button onClick={shareViaWhatsApp} className="popup-button whatsapp-button">
                    📱 שתף בוואטסאפ
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  if (!currentQuestion) {
    return <div className="verb-tense-game loading">טוען משחק...</div>;
  }

  return (
    <div className="verb-tense-game mobile-three-lock-layout">
      <div className="game-header">
        <button onClick={handleBackToGames} className="back-to-verbs-button">
         ↩ חזרה למשחקי הפועל
        </button>
        <h1>מהו הזמן?</h1>
        <p className="game-instructions-animate">גרור את היד לשעון הנכון</p>
      </div>

      <div className="tense-game-board">
        <div className="locks-row locks-row-top">
          {FORMS.filter(form => form.id !== 'ציווי').map((form, index) => (
            <div
              key={form.id}
              data-form-id={form.id}
              data-lock-name={form.name}
              className={`lock lock-position-${index} ${
                selectedLock === form.id ? (feedback === 'correct' ? 'unlocked' : 'wrong-shake') : ''
              } ${hoveredLock === form.id ? 'hovered' : ''}`}
            >
              <span className="lock-icon" aria-hidden="true">
                <img className="lock-icon-image" src={alarmClockIcon} alt="" />
              </span>
              <div className="lock-label">{form.name}</div>
            </div>
          ))}
        </div>

        <div 
          className={`key-container ${draggedKey ? 'dragging' : ''} ${feedback === 'correct' ? 'correct-pulse' : ''}`}
          onPointerDown={handlePointerStart}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          style={{ touchAction: 'none' }}
        >
          <div className="key">
            <div className="key-icon">👈🏻</div>
            <div className="key-verb">{currentQuestion.verb}</div>
          </div>
          <div className="progress">{currentVerbIndex + 1}/{gameVerbs.length}</div>
        </div>

        <div className="locks-row locks-row-bottom">
          {FORMS.filter(form => form.id === 'ציווי').map((form) => (
            <div
              key={form.id}
              data-form-id={form.id}
              data-lock-name={form.name}
              className={`lock lock-bottom ${
                selectedLock === form.id ? (feedback === 'correct' ? 'unlocked' : 'wrong-shake') : ''
              } ${hoveredLock === form.id ? 'hovered' : ''}`}
            >
              <span className="lock-icon" aria-hidden="true">
                <img className="lock-icon-image" src={alarmClockIcon} alt="" />
              </span>
              <div className="lock-label">{form.name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default VerbTenseGame1;
